import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { useAuthStore } from './store/authStore';
import AdminLayout from './components/layout/AdminLayout';
import PageLoader from './components/ui/PageLoader';

const Login = lazy(() => import('./pages/Login'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const ContentList = lazy(() => import('./pages/content/ContentList'));
const ContentEditor = lazy(() => import('./pages/content/ContentEditor'));
const LeadsPage = lazy(() => import('./pages/leads/LeadsPage'));
const MediaPage = lazy(() => import('./pages/media/MediaPage'));
const SettingsPage = lazy(() => import('./pages/settings/SettingsPage'));
const UsersPage = lazy(() => import('./pages/settings/UsersPage'));
const I18nPage = lazy(() => import('./pages/settings/I18nPage'));
const TranslationsManager = lazy(() => import('./pages/translations/TranslationManager'));
const CompaniesPage = lazy(() => import('./pages/companies/CompaniesPage'));

function AuthGuard({ children }) {
  const isAuth = useAuthStore((s) => s.isAuth);
  return isAuth ? children : <Navigate to="/login" replace />;
}
function GuestGuard({ children }) {
  const isAuth = useAuthStore((s) => s.isAuth);
  return !isAuth ? children : <Navigate to="/dashboard" replace />;
}

export default function App() {
  return (
    <BrowserRouter>
      <Toaster position="top-right"
        toastOptions={{
          style: { background: '#fff', color: '#0F172A', border: '1px solid #e2e8f0' },
          success: { iconTheme: { primary: '#22c55e', secondary: '#fff' } },
          error: { iconTheme: { primary: '#ef4444', secondary: '#fff' } },
        }}
      />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/login" element={<GuestGuard><Login /></GuestGuard>} />

          <Route path="/" element={<AuthGuard><AdminLayout /></AuthGuard>}>
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="dashboard" element={<Dashboard />} />

            <Route path="content/:company/:type" element={<ContentList />} />
            <Route path="content/:company/:type/new" element={<ContentEditor />} />
            <Route path="content/:company/:type/:id" element={<ContentEditor />} />

            <Route path="leads" element={<LeadsPage />} />
            <Route path="leads/:company" element={<LeadsPage />} />

            <Route path="media" element={<MediaPage />} />

            <Route path="companies" element={<CompaniesPage />} />

            <Route path="settings" element={<SettingsPage />} />
            <Route path="settings/users" element={<UsersPage />} />
            <Route path="settings/i18n" element={<I18nPage />} />
            <Route path="settings/translations" element={<TranslationsManager />} />
          </Route>

          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}